import React, { useEffect, useState } from 'react';

/**
 * Modal de avaliação de uma postagem.
 * Permite editar o texto e então: aprovar agora, agendar a aprovação (X horas) ou recusar.
 *
 * props:
 *  - postagem: objeto da postagem (ou null para ocultar)
 *  - processando: bool (desabilita o confirmar enquanto salva)
 *  - onClose(): fecha sem ação
 *  - onConfirmar(payload): { acao, texto, horas_agendamento?, motivo? }
 */
export default function ModalAcao({ postagem, processando, onClose, onConfirmar }) {
    const [texto, setTexto] = useState('');
    const [modo, setModo] = useState('aprovar'); // 'aprovar' | 'agendar' | 'recusar'
    const [horas, setHoras] = useState(2);
    const [motivo, setMotivo] = useState('');

    // Reinicializa o formulário sempre que abrir uma nova postagem.
    useEffect(() => {
        if (!postagem) return;
        setTexto(postagem.texto ?? '');
        setModo('aprovar');
        setHoras(2);
        setMotivo('');
    }, [postagem]);

    if (!postagem) return null;

    const textoInvalido = !texto.trim() || texto.length > 100;
    const horasInvalida = modo === 'agendar' && (!horas || horas < 1 || horas > 168);
    const motivoInvalido = modo === 'recusar' && (!motivo.trim() || motivo.length > 500);
    const podeConfirmar =
        !processando &&
        (modo === 'recusar' ? !motivoInvalido : (!textoInvalido && !horasInvalida));

    const confirmar = () => {
        if (!podeConfirmar) return;

        if (modo === 'recusar') {
            onConfirmar({ acao: 'recusar', motivo: motivo.trim() });
            return;
        }

        onConfirmar({
            acao: 'aprovar',
            texto: texto.trim(),
            horas_agendamento: modo === 'agendar' ? Number(horas) : 0,
        });
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="card"
                style={{ width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}
            >
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Avaliar postagem</strong>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="card-body">
                    <div className="text-center mb-3">
                        <img
                            src={postagem.imagem}
                            alt="Imagem da postagem"
                            style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #ddd' }}
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            Texto <small className="text-muted">({texto.length}/100)</small>
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            maxLength={100}
                            value={texto}
                            disabled={modo === 'recusar'}
                            onChange={(e) => setTexto(e.target.value)}
                        />
                        <small className="text-muted">
                            O texto editado sobrescreve o original ao aprovar.
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Ação</label>
                        <div className="btn-group btn-group-toggle d-flex" role="group">
                            <button
                                type="button"
                                className={`btn btn-sm ${modo === 'aprovar' ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={() => setModo('aprovar')}
                            >
                                Aprovar agora
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${modo === 'agendar' ? 'btn-info' : 'btn-outline-info'}`}
                                onClick={() => setModo('agendar')}
                            >
                                Agendar
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${modo === 'recusar' ? 'btn-danger' : 'btn-outline-danger'}`}
                                onClick={() => setModo('recusar')}
                            >
                                Recusar
                            </button>
                        </div>
                    </div>

                    {modo === 'agendar' && (
                        <div className="form-group">
                            <label>Aprovar daqui a quantas horas? <small className="text-muted">(1 a 168)</small></label>
                            <input
                                type="number"
                                className="form-control"
                                min={1}
                                max={168}
                                value={horas}
                                onChange={(e) => setHoras(e.target.value)}
                            />
                            <small className="text-muted">
                                A janela de 24h no feed só começa a contar a partir da aprovação automática.
                            </small>
                        </div>
                    )}

                    {modo === 'recusar' && (
                        <div className="form-group">
                            <label>
                                Motivo da recusa <small className="text-muted">({motivo.length}/500)</small>
                            </label>
                            <textarea
                                className="form-control"
                                rows={3}
                                maxLength={500}
                                value={motivo}
                                placeholder="Explique ao operador o motivo da recusa..."
                                onChange={(e) => setMotivo(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                <div className="card-footer d-flex justify-content-end">
                    <button type="button" className="btn btn-sm btn-outline-secondary mr-2" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${modo === 'recusar' ? 'btn-danger' : 'btn-success'}`}
                        disabled={!podeConfirmar}
                        onClick={confirmar}
                    >
                        {processando
                            ? 'Salvando...'
                            : modo === 'recusar' ? 'Confirmar recusa'
                            : modo === 'agendar' ? 'Agendar aprovação'
                            : 'Aprovar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
